import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import './Segments.css';
import { Utils, fetchWithAuth, formatDate, getDefaultPageSize, getTotalPages, can, exportListCsv, FUNNEL_SEGMENT_RULE_ID, FUNNEL_MODE_CODES, labelizeKey, formatDateTime } from '../../utils';
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";
import { IoEye } from "react-icons/io5";
import { BsPencil, BsClockHistory } from "react-icons/bs";
import { MdDeleteOutline, MdAddCircle, MdStarBorder, MdOutlineAppSettingsAlt, MdFileDownload } from "react-icons/md";
import { LuCopy } from "react-icons/lu";
import Swal from "sweetalert2";
import {
  FiBarChart2,
  FiChevronDown,
  FiGlobe,
  FiMail,
  FiMapPin,
  FiRefreshCw,
  FiSearch,
  FiSettings,
  FiSmartphone,
  FiTag,
  FiThumbsUp,
  FiUser,
} from "react-icons/fi";

/** Plain-words condition of a funnel-users rule: which funnel step, who, dates, filters and when the list was saved. */
const FunnelUsersRuleNote = ({ filter }) => {
  const count = (filter.userIds || []).length;
  const src = filter.source;
  const steps = (src?.steps || []).map(labelizeKey);
  const i = src?.step_index;
  const filterText = Object.entries(src?.filters || {})
    .map(([k, v]) => `${k === 'is_organic' ? 'source' : k.replace('_id', '').replace('_', ' ')}: ${k === 'is_organic' ? (String(v) === '1' ? 'Organic' : 'Campaign') : v}`)
    .join(', ');
  return (
    <div className="filter-row text-muted d-block">
      {src && steps.length > 0 && Number.isInteger(i) ? (
        <>
          <div>
            <strong>Condition:</strong>{' '}
            {src.kind === 'all'
              ? `Everyone who did ${steps[0]}`
              : src.kind === 'dropped'
              ? `Users who did ${steps[i]} but never did ${steps[i + 1] || 'the next step'}`
              : `Users who did ${steps[i]}${i > 0 ? ' (after the earlier steps)' : ''}`}
          </div>
          <div><strong>Funnel:</strong> {steps.join(' → ')} ({(FUNNEL_MODE_CODES.find((m) => m.key === src.mode)?.label || 'In order').toLowerCase()}{src.window_days ? `, within ${src.window_days} days` : ', no time limit'})</div>
          {src.group && <div><strong>Group:</strong> {src.group}{src.breakdown ? ` (by ${src.breakdown})` : ''}</div>}
          {src.date_from && <div><strong>Entered between:</strong> {src.date_from} – {src.date_to}</div>}
          {filterText && <div><strong>Filters:</strong> {filterText}</div>}
          {src.created_at && <div><strong>Saved on:</strong> {formatDateTime(src.created_at)}</div>}
        </>
      ) : (
        <div>Users picked from a funnel step.</div>
      )}
      <div>
        Fixed list of {count} {count === 1 ? 'user' : 'users'}{filter.total > count ? ` (newest ${count} of ${filter.total})` : ''}. It does not update on its own. Rename or pause the segment, or remove this rule and build your own.
      </div>
    </div>
  );
};

const Segments = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [segmentName, setSegmentName] = useState('');
  const [expandedRules, setExpandedRules] = useState([]); // eslint-disable-line no-unused-vars
  const [addedFilters, setAddedFilters] = useState([]);
  const [showAndFilterSelector, setShowAndFilterSelector] = useState(false);
  const [subscriptionCount, setSubscriptionCount] = useState('Pending');
  const [isCountingSubscriptions, setIsCountingSubscriptions] = useState(false);
  const [segments, setSegments] = useState([]);
  const [segmentCounts, setSegmentCounts] = useState({}); // lazy-loaded counts: { [segmentId]: number }
  const [tabCounts, setTabCounts] = useState({ all: 0, active: 0, paused: 0 });
  const tableScrollRef = React.useRef(null);
  const tableInnerRef = React.useRef(null);
  const [editingSegmentId, setEditingSegmentId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [availableStates, setAvailableStates] = useState([]);
  const [showQuickstart, setShowQuickstart] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = getDefaultPageSize();
  const [segmentsTotal, setSegmentsTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const getSegmentActionOptionCount = () =>
    1 +
    (can("segments.edit") ? 3 : 0) +
    (can("segments.add") ? 1 : 0) +
    (can("segments.delete") ? 1 : 0);
  const { openActionItem, dropdownPosition, dropdownRef, closeDropdown, handleOpenDropdown } = useActionDropdown({
    tableScrollRef,
    optionCount: getSegmentActionOptionCount,
    menuWidth: 220,
  });
  const tableMaxHeight = useTableMaxHeight(tableScrollRef, 240, 48, { deps: [showQuickstart] });
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, segments?.length);

  const renderIcon = (icon, size = 16) => {
    if (!icon) return null;
    // Backward-compatible: old saved filters may store emoji/string icons
    if (typeof icon === "string") return <span style={{ fontSize: size, lineHeight: 1 }}>{icon}</span>;
    const IconCmp = icon;
    return <IconCmp size={size} style={{ flexShrink: 0 }} />;
  };

  const getFilterIcon = (filter) => {
    const ruleId = filter?.ruleId || filter?.rule?.id;
    // if (filter?.rule?.icon) return filter.rule.icon; // prefer explicit icon if present
    switch (ruleId) {
      case "user-tag":
        return FiTag;
      case "location":
        return FiMapPin;
      case "state":
        return FiGlobe;
      case "message-event":
        return FiMail;
      case "first-session":
        return BsClockHistory;
      case "last-session":
        return FiRefreshCw;
      case "session-count":
        return FiBarChart2;
      case "app_version":
        return MdOutlineAppSettingsAlt;
      case "device_type":
        return FiSmartphone;
      case "user-type":
        return FiUser;
      default:
        return FiTag;
    }
  };

  const segmentCards = [
    {
      title: 'First-time Audience',
      description: 'Connect with new contacts that joined in the past 3 hours',
      icon: FiSmartphone,
      color: '#E8F0FE'
    },
    {
      title: 'Active Audience',
      description: 'Engage recently active contacts',
      icon: FiThumbsUp,
      color: '#E8F0FE'
    },
    {
      title: 'Regional Audience',
      description: 'Contact your active audience from a specified country',
      icon: FiMapPin,
      color: '#E8F0FE'
    },
    {
      title: 'Custom Audience',
      description: 'Reach targeted audience based on user properties or events from data tags',
      icon: FiTag,
      color: '#E8F0FE'
    }
  ];

  // eslint-disable-next-line no-unused-vars
  const segmentData = [
    {
      name: 'DASHO FEMALE',
      tags: ['User Tag dasho_customer is true', 'User Tag gender is female'],
      type: 'user',
      status: 'Active',
      pushSubs: 265,
      smsSubs: 0,
      emailSubs: 0,
      users: '-'
    },
    {
      name: 'DASHO MALE',
      tags: ['User Tag dasho_customer is true', 'User Tag gender is male'],
      type: 'user',
      status: 'Active',
      pushSubs: 1058,
      smsSubs: 0,
      emailSubs: 0,
      users: '-'
    }
  ];

  const segmentRules = [
    {
      id: 'first-session',
      icon: BsClockHistory,
      title: 'First Session',
      description: 'The first date/time the device communicated with our servers'
    },
    {
      id: 'last-session',
      icon: FiRefreshCw,
      title: 'Last Session',
      description: 'The most recent date/time the device communicated with our servers'
    },
    {
      id: 'session-count',
      icon: FiBarChart2,
      title: 'Session Count',
      description: 'Total number of times the device has opened your app or website'
    },
    // {
    //   id: 'language',
    //   icon: FiGlobe,
    //   title: 'Language',
    //   description: 'The language of the user\'s device'
    // },
    {
      id: 'user-tag',
      icon: FiTag,
      title: 'User Tag',
      description: 'Tags you have added to the user\'s device'
    },
    {
      id: 'message-event',
      icon: FiMail,
      title: 'Message Event',
      description: 'Filter by message interactions'
    },
    {
      id: 'state',
      icon: FiGlobe,
      title: 'State',
      description: 'Filter by user state/region'
    },
    {
      id: 'location',
      icon: FiMapPin,
      title: 'Location',
      description: 'Radius in meters from a particular geocoordinate (lat, long)'
    },
    {
      id: 'app_version',
      icon: MdOutlineAppSettingsAlt,
      title: 'App Version',
      description: 'Version of your app'
    },
    {
      id: 'device_type',
      icon: FiSmartphone,
      title: 'Device Type',
      description: 'Device operating system'
    },
    {
      id: 'user-type',
      icon: FiUser,
      title: 'User Type',
      description: 'Guest (anonymous, not logged in) or logged-in user'
    }
  ];

  const handleOpenModal = () => {
    setIsEditMode(false);
    setEditingSegmentId(null);
    setIsModalOpen(true);
    const currentDate = formatDate(new Date());
    setSegmentName(`${currentDate} Segment`);
  };

  const handleQuickstartSegment = (cardTitle) => {
    setIsEditMode(false);
    setEditingSegmentId(null);

    if (cardTitle === 'First-time Audience') {
      setSegmentName('First-time audience');
      const firstSessionRule = segmentRules.find(r => r.id === 'first-session');
      const prefilledFilter = {
        id: Date.now(),
        ruleId: 'first-session',
        rule: firstSessionRule,
        operator: 'time elapsed less than',
        key: '',
        value: '3.0',
        timeUnit: 'hours ago',
        type: 'and'
      };
      setAddedFilters([prefilledFilter]);
    } else if (cardTitle === 'Active Audience') {
      setSegmentName('Active Audience');
      const messageEventRule = segmentRules.find(r => r.id === 'message-event');
      const prefilledFilter = {
        id: Date.now(),
        ruleId: 'message-event',
        rule: messageEventRule,
        messageType: 'Any push notification',
        hasCondition: 'has',
        eventType: 'been clicked',
        countCondition: 'at least',
        countValue: '1',
        timeCondition: 'within',
        timePeriod: 'last 7 days',
        type: 'and'
      };
      setAddedFilters([prefilledFilter]);
    } else if (cardTitle === 'Regional Audience') {
      setSegmentName('Audience in [state]');
      // Fetch available states
      fetchStates();
      const stateRule = segmentRules.find(r => r.id === 'state');
      const lastSessionRule = segmentRules.find(r => r.id === 'last-session');
      const stateFilter = {
        id: Date.now(),
        ruleId: 'state',
        rule: stateRule,
        operator: 'is',
        stateValue: '',
        type: 'and'
      };
      const lastSessionFilter = {
        id: Date.now() + 1,
        ruleId: 'last-session',
        rule: lastSessionRule,
        operator: 'time elapsed less than',
        value: '0',
        timeUnit: 'hours ago',
        type: 'and'
      };
      setAddedFilters([stateFilter, lastSessionFilter]);
    } else if (cardTitle === 'Custom Audience') {
      setSegmentName('Custom audience');
      const userTagRule = segmentRules.find(r => r.id === 'user-tag');
      const prefilledFilter = {
        id: Date.now(),
        ruleId: 'user-tag',
        rule: userTagRule,
        operator: 'is',
        key: '',
        value: '',
        type: 'and'
      };
      setAddedFilters([prefilledFilter]);
    }

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSegmentName('');
    setExpandedRules([]);
    setAddedFilters([]);
    setShowAndFilterSelector(false);
    setSubscriptionCount('Pending');
    setIsEditMode(false);
    setEditingSegmentId(null);
  };

  const addFilter = (rule) => {
    const isTimeBasedRule = ['first-session', 'last-session'].includes(rule.id);
    const isLocationRule = rule.id === 'location';
    const newFilter = {
      id: Date.now(),
      ruleId: rule.id,
      rule: rule,
      operator: isTimeBasedRule ? 'time elapsed less than' : 'is',
      key: '',
      value: rule.id === 'user-type' ? 'guest' : '',
      ...(isLocationRule && { latitude: '', longitude: '' }),
      timeUnit: isTimeBasedRule ? 'hours ago' : undefined,
      type: 'and'
    };
    setAddedFilters(prev => [...prev, newFilter]);
    setShowAndFilterSelector(false);
    setSubscriptionCount('Pending');
  };

  const removeFilter = (filterId) => {
    setAddedFilters(prev => prev.filter(f => f.id !== filterId));
  };

  const updateFilter = (filterId, field, value) => {
    setAddedFilters(prev => prev.map(f =>
      f.id === filterId ? { ...f, [field]: value } : f
    ));
  };

  const fetchSubscriptionCount = useCallback(async () => {
    setIsCountingSubscriptions(true);
    try {
      const response = await fetchWithAuth(Utils.baseurl + 'count/segment/subscriptions', {
        method: 'POST',
        body: JSON.stringify({
          app_id: appId,
          filters: addedFilters
        }),
      });

      const result = await response.json();

      if (result.status) {
        setSubscriptionCount(result.count.toLocaleString());
      } else {
        setSubscriptionCount('Error');
      }
    } catch (error) {
      console.error('Error fetching subscription count:', error);
      setSubscriptionCount('Error');
    } finally {
      setIsCountingSubscriptions(false);
    }
  }, [appId, addedFilters]);

  const fetchSegments = useCallback(async () => {
    if (!appId) return;
    setListLoading(true);
    try {
      const response = await fetchWithAuth(Utils.baseurl + 'get/segments', {
        method: 'POST',
        body: JSON.stringify({
          app_id: appId,
          page: currentPage,
          pageSize,
          status: activeTab !== 'all' ? activeTab : undefined,
          search: String(searchQuery || '').trim() || undefined,
        }),
      });

      const result = await response.json();

      if (result.status) {
        setSegments(result.data || []);
        setSegmentsTotal(result.meta?.total ?? (result.data?.length || 0));
        if (result.counts) {
          setTabCounts({
            all: Number(result.counts.all) || 0,
            active: Number(result.counts.active) || 0,
            paused: Number(result.counts.paused) || 0,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching segments:', error);
    } finally {
      setListLoading(false);
    }
  }, [appId, currentPage, pageSize, activeTab, searchQuery]);
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery]);

  useEffect(() => {
    fetchSegments();
  }, [fetchSegments]);

  // Lazy/background counts: after segments load, fetch live count per segment (does not block list display)
  const segmentIdsKey = segments.map(s => s.id).join(',');
  useEffect(() => {
    if (!appId || !segments.length) return;

    setSegmentCounts({});
    const filtersList = segments.map(s => {
      try {
        return Array.isArray(s.filters) ? s.filters : (s.filters ? JSON.parse(s.filters) : []);
      } catch {
        return [];
      }
    });

    const countPromises = segments.map((seg, i) =>
      fetchWithAuth(Utils.baseurl + 'count/segment/subscriptions', {
        method: 'POST',
        body: JSON.stringify({ app_id: appId, filters: filtersList[i] }),
      })
        .then(res => res.json())
        .then(data => ({ id: seg.id, count: data.status ? data.count : 0 }))
        .catch(() => ({ id: seg.id, count: 0 }))
    );

    Promise.all(countPromises).then(results => {
      const countsMap = Object.fromEntries(results.map(r => [r.id, r.count]));
      setSegmentCounts(prev => ({ ...prev, ...countsMap }));

      // Batch-save to DB only where runtime count differs from saved value
      const toUpdate = results
        .filter(r => {
          const seg = segments.find(s => s.id === r.id);
          return seg && Number(seg.subscription_count) !== r.count;
        })
        .map(r => ({ id: r.id, subscription_count: r.count }));

      if (toUpdate.length > 0) {
        fetchWithAuth(Utils.baseurl + 'update/segment/subscription-counts', {
          method: 'POST',
          body: JSON.stringify({ updates: toUpdate }),
        })
          .then(res => res.json())
          .then(data => {
            if (data.status) {
              setSegments(prev =>
                prev.map(seg => {
                  const u = toUpdate.find(x => x.id === seg.id);
                  return u ? { ...seg, subscription_count: u.subscription_count } : seg;
                })
              );
            }
          })
          .catch(err => console.error('Update segment counts:', err));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, segmentIdsKey]);

  const fetchStates = useCallback(async () => {
    if (!appId) return;

    try {
      const response = await fetchWithAuth(Utils.baseurl + 'get/states', {
        method: 'POST',
        body: JSON.stringify({ app_id: appId }),
      });

      const result = await response.json();

      if (result.status) {
        setAvailableStates(result.data);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  }, [appId]);

  const addOrFilter = () => {
    if (addedFilters.length > 0) {
      const newFilter = {
        id: Date.now(),
        ruleId: null,
        rule: null,
        operator: 'is',
        key: '',
        value: '',
        type: 'or'
      };
      setAddedFilters(prev => [...prev, newFilter]);
    }
  };

  const handleCreateSegment = async () => {
    if (!segmentName.trim()) {
      toast.warning('Please enter a segment name');
      return;
    }

    if (addedFilters.length === 0) {
      toast.warning('Please add at least one filter');
      return;
    }

    try {
      const endpoint = isEditMode ? 'update/segment' : 'create/segment';
      const requestBody = isEditMode
        ? {
          id: editingSegmentId,
          name: segmentName,
          filters: addedFilters
        }
        : {
          app_id: appId,
          name: segmentName,
          filters: addedFilters,
          status: 'active'
        };

      const response = await fetchWithAuth(Utils.baseurl + endpoint, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (result.status) {
        toast.success(isEditMode ? 'Segment updated successfully!' : 'Segment created successfully!');
        handleCloseModal();
        // Refresh segments list
        fetchSegments();
      } else {
        toast.error(`Error ${isEditMode ? 'updating' : 'creating'} segment: ` + result.message);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} segment:`, error);
      toast.error(`Error ${isEditMode ? 'updating' : 'creating'} segment. Please try again.`);
    }
  };


  const handleViewSubscriptions = (segment) => {
    navigate(`/apps/${appId}/subscriptions?segment_id=${segment.id}&segment_name=${encodeURIComponent(segment.name)}`);
    closeDropdown();
  };

  const handleEditSegment = (segment) => {
    const filters = segment.filters ? JSON.parse(segment.filters) : [];
    setIsEditMode(true);
    setEditingSegmentId(segment.id);
    setSegmentName(segment.name);
    setAddedFilters(filters);
    setIsModalOpen(true);
    closeDropdown();
  };

  const handlePauseSegment = async (segment) => {
    const newStatus = segment.status === 'active' ? 'paused' : 'active';
    try {
      const response = await fetchWithAuth(Utils.baseurl + 'update/segment/status', {
        method: 'POST',
        body: JSON.stringify({
          id: segment.id,
          status: newStatus
        }),
      });

      const result = await response.json();

      if (result.status) {
        toast.success(`Segment ${newStatus === 'active' ? 'activated' : 'paused'} successfully!`);
        fetchSegments();
      } else {
        toast.error('Error updating segment status');
      }
    } catch (error) {
      console.error('Error updating segment status:', error);
      toast.error('Error updating segment status');
    }
    closeDropdown();
  };

  const handleSetAsDefault = async (segment) => {
    // TODO: Implement set as default functionality
    toast.info('Set as Default feature is not yet implemented');
    closeDropdown();
  };

  const handleDuplicateSegment = async (segment) => {
    try {
      const response = await fetchWithAuth(Utils.baseurl + 'create/segment', {
        method: 'POST',
        body: JSON.stringify({
          app_id: appId,
          name: `${segment.name} (Copy)`,
          filters: segment.filters ? JSON.parse(segment.filters) : [],
          status: 'active'
        }),
      });

      const result = await response.json();

      if (result.status) {
        toast.success('Segment duplicated successfully!');
        fetchSegments();
      } else {
        toast.error('Error duplicating segment');
      }
    } catch (error) {
      console.error('Error duplicating segment:', error);
      toast.error('Error duplicating segment');
    }
    closeDropdown();
  };

  const handleDeleteSegment = async (segment) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to delete "${segment.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
    });
    if (!confirm.isConfirmed) return;

    try {
      const response = await fetchWithAuth(Utils.baseurl + 'delete/segment', {
        method: 'POST',
        body: JSON.stringify({
          id: segment.id
        }),
      });

      const result = await response.json();

      if (result.status) {
        toast.success('Segment deleted successfully!');
        fetchSegments();
      } else {
        toast.error('Error deleting segment');
      }
    } catch (error) {
      console.error('Error deleting segment:', error);
      toast.error('Error deleting segment');
    }
    closeDropdown();
  };

  useEffect(() => {
    if (appId) {
      fetchSegments();
      fetchStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  // Watch for filter changes and update count
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!appId || addedFilters.length === 0) {
        setSubscriptionCount('Pending');
        return;
      }

      // Check if all filters have required values
      const hasIncompleteFilters = addedFilters.some(filter => {
        if (filter.ruleId === 'user-tag') {
          return !filter.key || (!filter.value && !['exists', 'does not exist'].includes(filter.operator));
        }
        if (filter.ruleId === 'location') {
          return !filter.latitude || !filter.longitude || !filter.value;
        }
        if (['first-session', 'last-session', 'session-count', 'app_version', 'device_type'].includes(filter.ruleId)) {
          return !filter.value && !['exists', 'does not exist'].includes(filter.operator);
        }
        return false;
      });

      if (hasIncompleteFilters) {
        setSubscriptionCount('Pending');
        return;
      }

      fetchSubscriptionCount();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedFilters, appId, fetchSubscriptionCount]);

  const totalRows = segmentsTotal || 0;
  const totalPages = getTotalPages(totalRows, pageSize);
  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("get/segments", {
        app_id: appId,
        status: activeTab !== "all" ? activeTab : undefined,
        search: String(searchQuery || "").trim() || undefined,
      }, "segments.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>Segments</h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={fetchSegments}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
            {can("segments.add") ? (
              <button type="button" className="add_app_btn btn_theme_primary" onClick={handleOpenModal}>
                <MdAddCircle />
                <span>New Segment</span>
              </button>
            ) : null}
          </div>
        </div>

      {/* Quickstart Section */}
      <div className="quickstart-section">
        <div className="quickstart-header" onClick={() => setShowQuickstart((v) => !v)} style={{ cursor: "pointer" }}>
          <span className="chevron" style={{ display: "inline-flex", transform: showQuickstart ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.2s" }}>
            {renderIcon(FiChevronDown, 16)}
          </span>
          <span className="quickstart-text">Try one of our quickstart segments</span>
        </div>

        {showQuickstart && (
          <div className="segment-cards">
            {segmentCards.map((card, index) => (
              <div key={index} className="segment-card">
                <div className="card-header">
                  <h3>{card.title}</h3>
                  <span className="card-icon">{renderIcon(card.icon, 18)}</span>
                </div>
                <p className="card-description">{card.description}</p>
                {can("segments.add") ? (
                <button className="btn-create" onClick={() => handleQuickstartSegment(card.title)}>Create</button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All <span className="tab-count">{tabCounts.all}</span>
        </button>
        <button
          className={`tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Active <span className="tab-count">{tabCounts.active}</span>
        </button>
        <button
          className={`tab ${activeTab === 'paused' ? 'active' : ''}`}
          onClick={() => setActiveTab('paused')}
        >
          Paused <span className="tab-count">{tabCounts.paused}</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-bar">
          <span className="search-icon">{renderIcon(FiSearch, 16)}</span>
          <input
            type="text"
            placeholder="Search by name or keywords"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button className="filter-btn">
          <span>{renderIcon(FiSettings, 16)}</span>
        </button>
      </div>

      {/* Table */}
      <div
        className="segments-table table_scroll_wrapper"
        ref={tableScrollRef}
      >
        <div
          ref={tableInnerRef}
          className={`table_inner table_y_scroll ${isAtScrollEnd ? 'at-scroll-end' : ''}`}
          style={{
            maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : 'calc(100vh - 320px)',
            overflowY: 'auto',
            overflowX: 'auto',
          }}
        >
          <table className="table_outer table-sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Push Subs</th>
                <th>SMS Subs</th>
                <th>Email Subs</th>
                <th>Users</th>
                <th className="col-action" style={{ textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {segments.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                    No segments found. Create your first segment!
                  </td>
                </tr>
              ) : (
                segments.map((segment, index) => {
                  const filters = segment.filters ? JSON.parse(segment.filters) : [];
                  return (
                    <tr key={index}>
                      <td>
                        <div className="segment-name">
                          <strong>{segment.name}</strong>
                          {filters.map((filter, i) => (
                            <div key={i} className="tag">
                              <span className="tag-icon">{renderIcon(getFilterIcon(filter), 14)}</span>
                              {filter.ruleId === 'user-tag'
                                ? `User Tag ${filter.key} ${filter.operator} ${filter.value}`
                                : filter.ruleId === FUNNEL_SEGMENT_RULE_ID
                                  ? `Funnel users: ${filter.label || 'selected users'} (${(filter.userIds || []).length} ${(filter.userIds || []).length === 1 ? 'user' : 'users'})`
                                : filter.ruleId === 'location'
                                  ? `Location lat: ${filter.latitude || '-'}, long: ${filter.longitude || '-'}, radius: ${filter.value || '-'}m`
                                  : filter.ruleId === 'user-type'
                                    ? `User Type ${filter.operator || 'is'} ${filter.value === 'logged_in' ? 'Logged-in' : 'Guest'}`
                                  : `${filter.rule?.title || 'Filter'} ${filter.operator} ${filter.value || filter.stateValue || ''}`
                              }
                            </div>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span className="type-icon">{renderIcon(FiUser, 16)}</span>
                      </td>
                      <td>
                        <span className={`status-badge ${segment.status}`}>
                          {segment.status.charAt(0).toUpperCase() + segment.status.slice(1)}
                        </span>
                      </td>
                      <td>
                        {segmentCounts[segment.id] !== undefined
                          ? Number(segmentCounts[segment.id]).toLocaleString()
                          : '...'}
                      </td>
                      <td>0</td>
                      <td>0</td>
                      <td>-</td>
                      <td className="col-action" style={{ position: 'relative', textAlign: 'right' }}>
                        <button
                          className="menu-btn"
                          onClick={(e) => handleOpenDropdown(e, segment.id, segment)}
                        >
                          <svg viewBox="-4 0 16 16" fill="currentColor" style={{ width: 16, height: 16 }}>
                            <path d="M8 5.25A1.625 1.625 0 108 2a1.625 1.625 0 000 3.25zM8 9.625a1.625 1.625 0 100-3.25 1.625 1.625 0 000 3.25zM9.625 12.375a1.625 1.625 0 11-3.25 0 1.625 1.625 0 013.25 0z" fill="currentColor" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <ListPaginationBar
          page={safePage}
          setPage={setCurrentPage}
          total={totalRows}
          pageSize={pageSize}
          totalPages={totalPages}
          ariaLabel="Segments pagination"
          loading={listLoading}
        />
      </div>

      {openActionItem && dropdownPosition && createPortal(
          <div
            className="segment-action-menu"
            ref={dropdownRef}
            style={{
              position: 'fixed',
              left: dropdownPosition.left,
              top: dropdownPosition.top,
              zIndex: 10000,
              minWidth: '220px',
            }}
          >
            <div className="dropdown-menu-subscription">
              <button
                type="button"
                onClick={() => handleViewSubscriptions(openActionItem)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
              >
                <IoEye size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.875rem' }}>View Subscriptions</span>
              </button>
              {can("segments.edit") ? (
              <button
                type="button"
                onClick={() => handleEditSegment(openActionItem)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
              >
                <BsPencil size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.875rem' }}>Edit</span>
              </button>
              ) : null}
              {can("segments.edit") ? (
              <button
                type="button"
                onClick={() => handlePauseSegment(openActionItem)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
              >
                <BsClockHistory size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.875rem' }}>{openActionItem.status === 'active' ? 'Pause' : 'Activate'}</span>
              </button>
              ) : null}
              {can("segments.edit") ? (
              <button
                type="button"
                onClick={() => handleSetAsDefault(openActionItem)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
              >
                <MdStarBorder size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.875rem' }}>Set as Default</span>
              </button>
              ) : null}
              {can("segments.add") ? (
              <button
                type="button"
                onClick={() => handleDuplicateSegment(openActionItem)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
              >
                <LuCopy size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.875rem' }}>Duplicate</span>
              </button>
              ) : null}
              {can("segments.delete") ? (
              <button
                type="button"
                className="delete-class"
                onClick={() => handleDeleteSegment(openActionItem)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', color: 'red' }}
              >
                <MdDeleteOutline size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.875rem' }}>Delete</span>
              </button>
              ) : null}
            </div>
          </div>,
          document.body
      )}

      {/* Segment Editor Modal */}
      <Modal
        show={isModalOpen}
        onHide={handleCloseModal}
        size="sm"
        centered
        className="segment-editor-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Segment Editor</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="modal-body-content">
            <div className="modal-full-width">
              <div className="form-row-with-status">
                <div className="form-group-inline">
                  <label>
                    Name of segment <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={segmentName}
                    onChange={(e) => setSegmentName(e.target.value)}
                    placeholder="Enter segment name"
                    className="segment-name-input"
                  />
                </div>

                <div className="subscribed-records">
                  <p className="records-label">Subscribed Records</p>
                  <div className="records-status">
                    {isCountingSubscriptions ? 'Calculating...' : subscriptionCount}
                  </div>
                </div>
              </div>

              <div className="rules-section">
                <p className="rules-title">Subscription records in this segment must meet these rules:</p>

                {/* Added Filters */}
                {addedFilters.map((filter, index) => (
                  <div key={filter.id} className="added-filter-container">
                    {filter.type === 'or' && index > 0 && (
                      <div className="or-filter-label">Or Filter</div>
                    )}
                    {filter.type === 'and' && index > 0 && (
                      <div className="and-label">AND</div>
                    )}

                    <div className="added-filter">
                      <div className="filter-header">
                        <div className="filter-title-section">
                          <span className="filter-icon">{renderIcon(getFilterIcon(filter), 14)}</span>
                          <span className="filter-name">{filter.rule?.title || 'Filter'}</span>
                        </div>
                        <button
                          className="filter-remove-btn"
                          onClick={() => removeFilter(filter.id)}
                        >
                          ×
                        </button>
                      </div>

                      <div className="filter-config">
                        {/* Message Event Filter UI */}
                        {filter.ruleId === 'message-event' && (
                          <>
                            <div className="filter-row">
                              <select
                                value={filter.messageType || 'Any push notification'}
                                onChange={(e) => updateFilter(filter.id, 'messageType', e.target.value)}
                                className="filter-select"
                              >
                                <option value="Any push notification">Any push notification</option>
                                <option value="Any in-app message">Any in-app message</option>
                                <option value="Any email">Any email</option>
                                <option value="Any SMS">Any SMS</option>
                              </select>
                              <select
                                value={filter.hasCondition || 'has'}
                                onChange={(e) => updateFilter(filter.id, 'hasCondition', e.target.value)}
                                className="filter-select"
                              >
                                <option value="has">has</option>
                                <option value="has not">has not</option>
                              </select>
                              <select
                                value={filter.eventType || 'been clicked'}
                                onChange={(e) => updateFilter(filter.id, 'eventType', e.target.value)}
                                className="filter-select"
                              >
                                <option value="been clicked">been clicked</option>
                                <option value="been received">been received</option>
                                <option value="been sent">been sent</option>
                              </select>
                            </div>
                            <div className="filter-row">
                              <select
                                value={filter.countCondition || 'at least'}
                                onChange={(e) => updateFilter(filter.id, 'countCondition', e.target.value)}
                                className="filter-select"
                              >
                                <option value="at least">at least</option>
                                <option value="at most">at most</option>
                                <option value="exactly">exactly</option>
                              </select>
                              <input
                                type="text"
                                placeholder="1"
                                value={filter.countValue || '1'}
                                onChange={(e) => updateFilter(filter.id, 'countValue', e.target.value)}
                                className="filter-input filter-count-value"
                              />
                              <span className="filter-label-text">times</span>
                              <select
                                value={filter.timeCondition || 'within'}
                                onChange={(e) => updateFilter(filter.id, 'timeCondition', e.target.value)}
                                className="filter-select"
                              >
                                <option value="within">within</option>
                                <option value="between">between</option>
                              </select>
                            </div>
                            <div className="filter-row">
                              <select
                                value={filter.timePeriod || 'last 7 days'}
                                onChange={(e) => updateFilter(filter.id, 'timePeriod', e.target.value)}
                                className="filter-select"
                              >
                                <option value="last 24 hours">last 24 hours</option>
                                <option value="last 7 days">last 7 days</option>
                                <option value="last 30 days">last 30 days</option>
                                <option value="last 90 days">last 90 days</option>
                              </select>
                            </div>
                          </>
                        )}

                        {/* State Filter UI */}
                        {filter.ruleId === 'state' && (
                          <div className="filter-row">
                            <select
                              value={filter.operator || 'is'}
                              onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                              className="filter-select"
                            >
                              <option value="is">is</option>
                              <option value="is not">is not</option>
                            </select>
                            <select
                              value={filter.stateValue || ''}
                              onChange={(e) => {
                                updateFilter(filter.id, 'stateValue', e.target.value);
                                // Update segment name with selected state
                                if (e.target.value) {
                                  setSegmentName(`Audience in ${e.target.value}`);
                                }
                              }}
                              className="filter-select filter-state-select"
                            >
                              <option value="">Select State</option>
                              {availableStates.map((state, idx) => (
                                <option key={idx} value={state}>{state}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Location Filter UI - latitude, longitude, radius (value) */}
                        {filter.ruleId === 'location' && (
                          <div className="filter-row filter-location-row">
                            <input
                              type="text"
                              placeholder="Latitude"
                              value={filter.latitude ?? ''}
                              onChange={(e) => updateFilter(filter.id, 'latitude', e.target.value)}
                              className="filter-input filter-latitude"
                            />
                            <input
                              type="text"
                              placeholder="Longitude"
                              value={filter.longitude ?? ''}
                              onChange={(e) => updateFilter(filter.id, 'longitude', e.target.value)}
                              className="filter-input filter-longitude"
                            />
                            <input
                              type="text"
                              placeholder="Radius (meters)"
                              value={filter.value ?? ''}
                              onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                              className="filter-input filter-value"
                            />
                          </div>
                        )}

                        {filter.ruleId === 'user-tag' && (
                          <>
                            <div className="filter-row">
                              <input
                                type="text"
                                placeholder="key"
                                value={filter.key}
                                onChange={(e) => updateFilter(filter.id, 'key', e.target.value)}
                                className="filter-input filter-key"
                              />
                            </div>
                            <div className="filter-help-text">
                              ⓘ Manually type in (exact match) or select tags pulled from your test users
                            </div>
                          </>
                        )}

                        {/* User Type Filter UI - guest (anonymous) / logged-in */}
                        {filter.ruleId === 'user-type' && (
                          <div className="filter-row">
                            <select
                              value={filter.operator || 'is'}
                              onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                              className="filter-select"
                            >
                              <option value="is">is</option>
                              <option value="is not">is not</option>
                            </select>
                            <select
                              value={filter.value || 'guest'}
                              onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                              className="filter-select"
                            >
                              <option value="guest">Guest (Anonymous)</option>
                              <option value="logged_in">Logged-in User</option>
                            </select>
                          </div>
                        )}

                        {filter.ruleId === FUNNEL_SEGMENT_RULE_ID && <FunnelUsersRuleNote filter={filter} />}

                        {!['message-event', 'state', 'location', 'user-type', FUNNEL_SEGMENT_RULE_ID].includes(filter.ruleId) && (
                          <div className="filter-row">
                            <select
                              value={filter.operator}
                              onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                              className="filter-select"
                            >
                              <option value="is">is</option>
                              <option value="is not">is not</option>
                              <option value="exists">exists</option>
                              <option value="does not exist">does not exist</option>
                              <option value="greater than">greater than</option>
                              <option value="less than">less than</option>
                              <option value="time elapsed greater than">time elapsed greater than</option>
                              <option value="time elapsed less than">time elapsed less than</option>
                            </select>

                            {!['exists', 'does not exist'].includes(filter.operator) && (
                              <input
                                type="text"
                                placeholder="value"
                                value={filter.value}
                                onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                                className="filter-input filter-value"
                              />
                            )}

                            {['time elapsed greater than', 'time elapsed less than'].includes(filter.operator) && (
                              <select
                                value={filter.timeUnit || 'hours ago'}
                                onChange={(e) => updateFilter(filter.id, 'timeUnit', e.target.value)}
                                className="filter-select filter-time-unit"
                              >
                                <option value="minutes ago">minutes ago</option>
                                <option value="hours ago">hours ago</option>
                                <option value="days ago">days ago</option>
                                <option value="weeks ago">weeks ago</option>
                              </select>
                            )}
                          </div>
                        )}

                        {/* Show subscription count for this filter */}
                        {(filter.ruleId === 'location'
                          ? (filter.latitude && filter.longitude && filter.value)
                          : filter.ruleId === 'user-tag'
                            ? (filter.key && filter.value)
                            : filter.value) && addedFilters.length > 0 && (
                            <div className="filter-count-text">
                              {isCountingSubscriptions ? (
                                'Calculating subscribed records...'
                              ) : subscriptionCount !== 'Pending' && subscriptionCount !== 'Error' ? (
                                `${subscriptionCount} subscribed records for this filter.`
                              ) : null}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add Filter and Add Or buttons */}
                {addedFilters.length > 0 && (
                  <div className="filter-actions">
                    <button className="btn-add-filter" onClick={() => setShowAndFilterSelector(!showAndFilterSelector)}>
                      <span className="add-icon">⊕</span> Add filter
                    </button>
                    <button className="btn-add-or" onClick={addOrFilter}>
                      <span className="add-icon">⊕</span> Add Or
                    </button>
                  </div>
                )}

                {/* And Filter Selector */}
                {showAndFilterSelector && (
                  <div className="and-filter-selector">
                    <div className="and-filter-header">
                      <span className="and-filter-label">And Filter</span>
                      <button
                        className="and-filter-close"
                        onClick={() => setShowAndFilterSelector(false)}
                      >
                        ×
                      </button>
                    </div>
                    <div className="and-filter-rules-list">
                      {segmentRules.map((rule) => (
                        <div key={rule.id} className="and-filter-rule-item">
                          <div className="and-filter-rule-left">
                            <span className="and-filter-rule-icon">{renderIcon(rule.icon, 16)}</span>
                            <span className="and-filter-rule-title">{rule.title}</span>
                          </div>
                          <div className="and-filter-rule-right">
                            <span className="and-filter-rule-description">{rule.description}</span>
                            <button
                              className="and-filter-rule-add-btn"
                              onClick={() => addFilter(rule)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Rules List */}
                <div className="rules-list">
                  {segmentRules.map((rule) => (
                    <div key={rule.id} className="rule-item">
                      <div className="rule-left">
                        <span className="rule-icon">{renderIcon(rule.icon, 16)}</span>
                        <span className="rule-title">{rule.title}</span>
                      </div>
                      <div className="rule-right">
                        <span className="rule-description">{rule.description}</span>
                        <button
                          className="rule-add-btn"
                          onClick={() => addFilter(rule)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          {((isEditMode && can("segments.edit")) || (!isEditMode && can("segments.add"))) ? (
          <button className="btn-create-segment" onClick={handleCreateSegment}>
            {isEditMode ? 'Update Segment' : 'Create Segment'}
          </button>
          ) : null}
        </Modal.Footer>
      </Modal>
      </div>
    </div>
  );
};

export default Segments;
